import { useState } from 'react';
import toast from 'react-hot-toast';
import { useFetchCallback } from '@services/services';
import { useSubscription } from './useSubscriptions';
import { SUBSCRIPTION_TYPES_MAP } from '@utils/constants';

/**
 * PRD-014 FR-28 — Métodos de pago soportados al activar/cambiar de plan.
 * Sin texto libre: el usuario solo elige entre estas dos opciones.
 *
 * Default: 'tarjeta'. La transferencia se de-prioriza a propósito (feedback
 * 2026-08-22): el cobro automático con tarjeta es el flujo normal; la
 * transferencia solo aparece como fallback para el cliente sin tarjeta. El
 * orden visual (tarjeta primero) lo controla `PaymentMethodSelector`.
 */
export type MetodoPago = 'tarjeta' | 'transferencia';

export const DEFAULT_METODO_PAGO: MetodoPago = 'tarjeta';

/**
 * Request al backend (createSubscription / convertFreemium).
 * El backend mapea metodo_pago sobre billing_mode flexible (charge_automatically
 * vs send_invoice). Ver PRD-014 §"Contrato de API — Activación de plan".
 */
interface ActivatePlanRequest {
  plan_id: string;
  metodo_pago: MetodoPago;
}

/**
 * Respuesta del backend (forma NORMALIZADA que consume la UI). La forma depende del metodo_pago:
 * - tarjeta       → { subscription_id, status: 'incomplete', client_secret }
 * - transferencia → { subscription_id, status, hosted_invoice_url }
 */
export interface ActivatePlanResponse {
  subscription_id: string;
  status: string;
  /** Presente cuando metodo_pago === 'tarjeta' (para confirmar con Stripe Elements). */
  client_secret?: string;
  /** Presente cuando metodo_pago === 'transferencia' (invoice de transferencia). */
  hosted_invoice_url?: string;
}

/**
 * createSubscription responde la forma plana de arriba, pero convertFreemium (tracking de
 * steps para poder reintentar desde el punto de falla) envuelve el éxito en
 * { message, completed_steps, data }. Mismo hook/tipo para ambos endpoints — normalizamos aquí
 * en vez de duplicar el unwrap en cada consumidor (este hook + la réplica del claim del partner
 * en useClaimPlans.ts, que NO puede usar este hook porque no monta Redux/Auth).
 */
interface ConvertFreemiumEnvelope {
  message: string;
  completed_steps: string[];
  data: ActivatePlanResponse;
}
export type RawActivatePlanResponse = ActivatePlanResponse | ConvertFreemiumEnvelope;

export const normalizeActivateResponse = (raw: RawActivatePlanResponse): ActivatePlanResponse =>
  'data' in raw ? raw.data : raw;

// Hook que dispara la activación/cambio de plan contra el backend.
//
// Usa convertFreemium cuando el plan actual es FREEMIUM (upgrade desde plan gratis),
// y createSubscription en cualquier otro caso (alta o cambio entre planes de pago).
//
// NO abre Stripe Checkout (window.open). Devuelve el payload para que la UI:
// - tarjeta: monte StripePaymentForm con el client_secret (3DS/SCA in-app).
// - transferencia: redirija/abra el hosted_invoice_url.
export const useActivatePlan = () => {
  const { planId } = useSubscription();
  const [isActivating, setIsActivating] = useState(false);

  const { call: callCreate } = useFetchCallback<ActivatePlanRequest, RawActivatePlanResponse>(
    'post',
    '/v1/suscriptions/createSubscription',
  );
  const { call: callConvert } = useFetchCallback<ActivatePlanRequest, RawActivatePlanResponse>(
    'post',
    '/v1/suscriptions/convertFreemium',
  );

  const activatePlan = async (
    planIdToActivate: string,
    metodoPago: MetodoPago = DEFAULT_METODO_PAGO,
  ): Promise<ActivatePlanResponse | null> => {
    const isFreemium = planId === SUBSCRIPTION_TYPES_MAP.FREEMIUM.key;
    const call = isFreemium ? callConvert : callCreate;

    const data: ActivatePlanRequest = {
      plan_id: planIdToActivate,
      metodo_pago: metodoPago,
    };

    setIsActivating(true);
    try {
      const response = await call({ data });
      return normalizeActivateResponse(response);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'No se pudo activar el plan. Intenta de nuevo.';
      toast.error(message);
      return null;
    } finally {
      setIsActivating(false);
    }
  };

  return { activatePlan, isActivating };
};
